/**
 * Pure helpers for the Catalyst widget thread (#1888).
 *
 * The widget keeps text chat and voice calls in one persisted thread. Each
 * message carries a `kind`:
 *   - `chat`        a typed turn or the greeting (default when absent)
 *   - `voice-turn`  one spoken turn from a call, grouped by `callId`
 *   - `call-status` one muted status row per call ("Voice call ended at ...")
 *
 * Only real turns reach the model. Call-status rows, error bubbles, the
 * greeting and legacy bracketed transcript rows never do.
 */

/** A KB source returned by the chat API and rendered as citation chips. */
export interface WidgetSource {
  type: string
  title: string
  href: string
  snippet: string
}

export type WidgetMessageKind = 'chat' | 'voice-turn' | 'call-status'

/** A single message in the widget thread. Persisted to localStorage. */
export interface WidgetMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  sources?: WidgetSource[]
  /** True when the API's final event carried isEscalation=true. */
  isEscalation?: boolean
  /** True when message rendering failed and we showed an error bubble. */
  isError?: boolean
  /** Absent on rows written before #1888; read as `chat`. */
  kind?: WidgetMessageKind
  /** Shared by the voice turns and the status row of one call. */
  callId?: string
}

/** A spoken turn as the voice SDK reports it. */
export interface SpokenTurn {
  id: string
  speaker: 'user' | 'agent'
  text: string
}

export interface ModelTurn {
  role: 'user' | 'assistant'
  content: string
}

export const GREETING_ID = 'greeting'

const LEGACY_TRANSCRIPT_PREFIX = '[Voice transcript:'
const LEGACY_BRACKETED_ENDED_PREFIX = '[Voice call ended'
const LEGACY_ENDED_PATTERN = /^Voice call ended at /

function isLegacyNotice(content: string): boolean {
  return content.startsWith(LEGACY_TRANSCRIPT_PREFIX) || content.startsWith(LEGACY_BRACKETED_ENDED_PREFIX)
}

/**
 * The history the chat route and the voice `_chat_context` receive. Keeps
 * typed turns and voice turns in order. Drops the greeting, error bubbles,
 * empty placeholders, call-status rows and legacy bracketed notices.
 */
export function buildModelHistory(messages: readonly WidgetMessage[]): ModelTurn[] {
  return messages
    .filter(
      (m) =>
        m.id !== GREETING_ID &&
        !m.isError &&
        m.kind !== 'call-status' &&
        m.content.trim() !== '' &&
        !isLegacyNotice(m.content)
    )
    .map(({ role, content }) => ({ role, content }))
}

/**
 * Repairs a thread read from localStorage. Drops legacy bracketed notices,
 * turns legacy "Voice call ended at" bubbles into status rows, and keeps the
 * first row for each id so a duplicated greeting renders once.
 */
export function normaliseStoredMessages(messages: readonly WidgetMessage[]): WidgetMessage[] {
  const seen = new Set<string>()
  const out: WidgetMessage[] = []
  for (const m of messages) {
    if (seen.has(m.id)) continue
    seen.add(m.id)
    if (isLegacyNotice(m.content)) continue
    if (m.kind === undefined && m.role === 'assistant' && LEGACY_ENDED_PATTERN.test(m.content)) {
      out.push({ ...m, kind: 'call-status' })
      continue
    }
    out.push(m)
  }
  return out
}

/**
 * Builds the rows one finished call adds to the thread: each spoken turn
 * once, then exactly one status row. All rows share `callId`.
 */
export function buildCallRecord(
  callId: string,
  turns: readonly SpokenTurn[],
  statusText: string,
  newId: () => string
): WidgetMessage[] {
  const seen = new Set<string>()
  const rows: WidgetMessage[] = []
  for (const turn of turns) {
    if (seen.has(turn.id) || turn.text.trim() === '') continue
    seen.add(turn.id)
    rows.push({
      id: newId(),
      role: turn.speaker === 'agent' ? 'assistant' : 'user',
      content: turn.text,
      kind: 'voice-turn',
      callId,
    })
  }
  rows.push({ id: newId(), role: 'assistant', content: statusText, kind: 'call-status', callId })
  return rows
}

/**
 * The greeting text. A configured tenant greeting wins. A template with
 * unresolved `{{variables}}` falls back, because the widget has no values
 * to fill them.
 */
export function resolveGreeting(
  configured: string | undefined | null,
  agentName: string,
  brandName: string
): string {
  const trimmed = configured?.trim()
  if (trimmed && !trimmed.includes('{{')) return trimmed
  return `Hi, I'm ${agentName}, an AI assistant for ${brandName}. How can I help today?`
}
